import {ITariff, ITariffAssigment} from '../pages/Profile/types';

/**
 * Возвращает активный (оплаченный) тариф или null.
 * tariff может быть false (нет компании), undefined (профиль не загружен)
 * или объектом с active === null (тариф не активен).
 */
export const getActiveTariff = (
    tariff: ITariff | false | null | undefined,
): ITariffAssigment | null => (tariff ? tariff.active : null);

/** true, если у пользователя есть активный тариф. */
export const hasActiveTariff = (tariff: ITariff | false | null | undefined): boolean =>
    getActiveTariff(tariff) !== null;

/**
 * Форматирует сумму ("3600.00" | 3600) в рубли: "3 600 ₽".
 * При пустом/некорректном значении возвращает прочерк.
 */
export const formatAmount = (amount?: string | number | null): string => {
    if (amount === undefined || amount === null || amount === '') {
        return '—';
    }

    const value = typeof amount === 'string' ? parseFloat(amount) : amount;

    if (Number.isNaN(value)) {
        return '—';
    }

    return `${value.toLocaleString('ru-RU')} ₽`;
};

/**
 * Склонение слова «пользователь» по числу: 1 — пользователь,
 * 2-4 — пользователя, 5+ — пользователей.
 */
export const pluralUsers = (count: number): string => {
    const mod10 = count % 10;
    const mod100 = count % 100;

    if (mod10 === 1 && mod100 !== 11) {
        return 'пользователь';
    }

    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
        return 'пользователя';
    }

    return 'пользователей';
};

/** Склонение слова «день» по числу: 1 — день, 2-4 — дня, 5+ — дней. */
export const pluralDays = (count: number): string => {
    const mod10 = count % 10;
    const mod100 = count % 100;

    if (mod10 === 1 && mod100 !== 11) {
        return 'день';
    }

    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
        return 'дня';
    }

    return 'дней';
};

/** Дата в формате «11 апреля 2026» для баннеров и подписок. */
export const formatDateRuLong = (iso?: string | null): string => {
    if (!iso) {
        return '—';
    }

    const date = new Date(iso);

    if (Number.isNaN(date.getTime())) {
        return '—';
    }

    return new Intl.DateTimeFormat('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    }).format(date).replace(/\s*г\.$/, '');
};

export const formatDateRu = (iso?: string | null): string => {
    if (!iso) {
        return '—';
    }

    const date = new Date(iso);

    if (Number.isNaN(date.getTime())) {
        return '—';
    }

    return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(date);
};

export const formatPeriod = (startsAt?: string | null, endsAt?: string | null): string | null => {
    if (!startsAt || !endsAt) {
        return null;
    }

    return `${formatDateRu(startsAt)} — ${formatDateRu(endsAt)}`;
};

export const getClientTimezone = (): string =>
    Intl.DateTimeFormat().resolvedOptions().timeZone;
